import { z } from "zod";

/** A brand's research settings (Brand.research), set by its owners. */
export const researchSettingsSchema = z.object({
  /** Run the brief every Monday. Turned on when brand setup finishes. */
  weeklyEnabled: z.boolean().default(false),
  /** Research spend cap per brand per week, in USD (setup, briefs, topics and trials together). */
  weeklyBudgetUsd: z.number().min(0).max(100).default(2),
  /** Anything the researcher should always know, e.g. "We only serve Australia". */
  steer: z.string().trim().max(2000).default(""),
});
export type ResearchSettings = z.infer<typeof researchSettingsSchema>;

export const pillarSuggestionSchema = z.object({ name: z.string(), format: z.string() });

/** A pillar's "how posts are shaped" needs to be a sentence the writer can follow. */
export const PILLAR_FORMAT_MIN = 10;

/**
 * A suggested pillar's shape, if it's usable: a real sentence. Empty for a bare content type
 * ("article") or leaked model scaffolding, so the person writes it instead of fixing junk.
 */
export const usablePillarFormat = (text: string) => {
  const clean = text.trim();
  if (clean.length < PILLAR_FORMAT_MIN) return "";
  if (/raw assistant message|^[{[]/i.test(clean)) return "";
  return clean.slice(0, 600);
};

/** Brand setup progress, written by the researcher's setup run. */
export const setupStateSchema = z.object({
  status: z.enum(["running", "ready", "failed"]),
  runId: z.string(),
  category: z.string().default(""),
  pillars: z.array(pillarSuggestionSchema).default([]),
  pillarsDone: z.boolean().default(false),
  error: z.string().nullable().default(null),
});
export type SetupState = z.infer<typeof setupStateSchema>;

/** The colour jobs a brand kit names (BrandKit.tokens.roles). */
export const LOOK_ROLES = [
  "background",
  "surface",
  "text",
  "secondaryText",
  "accent",
  "highlight",
] as const;
export type LookRole = (typeof LOOK_ROLES)[number];

export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-f]{6}$/i, "A colour like #1d2330");

export const SOCIAL_PLATFORMS = [
  "linkedin",
  "instagram",
  "x",
  "tiktok",
  "facebook",
  "youtube",
  "threads",
  "reddit",
  "pinterest",
] as const;

/**
 * The brand's look as the brand designer found it on the website. It lives on the look run itself
 * (AgentRun.output of the brand's latest "brand_designer" run), so the researcher and the
 * designer never write the same JSON at once. A draft like the profile: used for nothing until a
 * person applies it to the brand kit.
 */
export const lookStateSchema = z.object({
  status: z.enum(["running", "ready", "failed"]),
  runId: z.string(),
  /** opening the site → reading it (colours, fonts, logo found) → naming (the designer) → done */
  stage: z.enum(["opening", "reading", "naming", "done"]).default("opening"),
  error: z.string().nullable().default(null),
  siteTitle: z.string().default(""),
  /** Private file keys (served from /api/files/<key>). */
  screenshotKey: z.string().nullable().default(null),
  logoKey: z.string().nullable().default(null),
  /** The colour the logo sat on, so a white logo isn't shown on white. */
  logoOn: hexColorSchema.nullable().default(null),
  colors: z
    .array(z.object({ hex: hexColorSchema, name: z.string().default(""), use: z.string() }))
    .default([]),
  roles: z.partialRecord(z.enum(LOOK_ROLES), hexColorSchema).default({}),
  fonts: z
    .object({
      display: z.string().default(""),
      body: z.string().default(""),
      all: z.array(z.string()).default([]),
    })
    .default({ display: "", body: "", all: [] }),
  icons: z
    .object({
      library: z.string().default(""),
      style: z.string().default(""),
      /** Small PNG data URIs of icons as the site draws them. */
      samples: z.array(z.string()).default([]),
    })
    .default({ library: "", style: "", samples: [] }),
  imagery: z.string().default(""),
  shapes: z.string().default(""),
  /** The look in a sentence or two, in plain words. */
  summary: z.string().default(""),
  /** Anything the person should know, e.g. why the colours weren't named. */
  note: z.string().default(""),
  social: z.array(z.object({ platform: z.enum(SOCIAL_PLATFORMS), url: z.string() })).default([]),
  appliedAt: z.string().nullable().default(null),
});
export type LookState = z.infer<typeof lookStateSchema>;

/** AgentRun.agent for brand-look runs. */
export const LOOK_AGENT = "brand_designer";

export const readResearch = (raw: unknown) => {
  const value = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const settings = researchSettingsSchema.safeParse(value);
  const setup = setupStateSchema.safeParse(value["setup"]);
  return {
    settings: settings.success ? settings.data : researchSettingsSchema.parse({}),
    setup: setup.success ? setup.data : null,
  };
};

export const ideaStatusSchema = z.enum(["new", "saved", "used", "dismissed"]);
export type IdeaStatus = z.infer<typeof ideaStatusSchema>;

/** The parts of the brand profile the researcher drafts at setup, in the order they're reviewed. */
export const SETUP_SECTIONS = ["PRODUCT", "PERSONAS", "COMPETITORS", "MARKET", "VOICE"] as const;
